import React, { useState, useMemo, useEffect } from 'react';
import {
  MapPin,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  Eye,
  Phone,
  Mail,
  Shield,
  Activity,
  BarChart3,
  PieChart,
  Calendar,
  Download,
  Settings,
  Bell,
  UserCheck,
  UserX,
  Clock as ClockIcon,
  Building,
  AlertCircle,
  Grid,
  List,
  Maximize2,
  Minimize2
} from 'lucide-react';

// Mock Data Generator for 1000 sites
const generateSiteData = () => {
  const sites = [];
  const regions = ['North', 'South', 'East', 'West', 'Central', 'Northeast', 'Northwest', 'Southeast', 'Southwest'];
  const siteTypes = ['Factory', 'Warehouse', 'Office', 'Retail', 'Logistics', 'Data Center', 'Research Lab', 'Service Center'];
  
  for (let i = 0; i < 1000; i++) {
    const region = regions[i % regions.length];
    const siteType = siteTypes[Math.floor(Math.random() * siteTypes.length)];
    const siteNumber = Math.floor(i / 10) + 1;
    
    // Generate random manpower data (10-15 people per site)
    const total = Math.floor(Math.random() * 6) + 10;
    const present = Math.floor(Math.random() * (total + 1));
    const absent = total - present;
    const onLeave = Math.floor(Math.random() * (absent + 1));
    const actualAbsent = absent - onLeave;
    
    // Calculate attendance rate
    const attendanceRate = total > 0 ? ((present) / total * 100).toFixed(1) : 0;
    
    // Determine status based on attendance rate
    let status = 'normal';
    if (attendanceRate < 60) status = 'critical';
    else if (attendanceRate < 80) status = 'warning';
    
    // Generate shift data
    const shifts = [];
    const shiftNames = ['Morning', 'Evening', 'Night', 'General'];
    const shiftCount = Math.floor(Math.random() * 3) + 2; // 2-4 shifts
    
    for (let s = 0; s < shiftCount; s++) {
      const shiftTotal = Math.floor(total / shiftCount) + (s === 0 ? total % shiftCount : 0);
      const shiftPresent = Math.floor(Math.random() * (shiftTotal + 1));
      shifts.push({
        name: shiftNames[s % shiftNames.length],
        total: shiftTotal,
        present: shiftPresent,
        absent: shiftTotal - shiftPresent
      });
    }
    
    sites.push({
      id: i + 1,
      code: `${region.substring(0, 2)}${siteType.substring(0, 2)}${String(siteNumber).padStart(3, '0')}`,
      name: `${region} ${siteType} ${siteNumber}`,
      region,
      type: siteType,
      total,
      present,
      absent: actualAbsent,
      onLeave,
      attendanceRate: parseFloat(attendanceRate),
      status,
      shifts,
      location: {
        lat: 28.6139 + (Math.random() - 0.5) * 2,
        lng: 77.2090 + (Math.random() - 0.5) * 2,
        address: `${region} Zone ${Math.floor(i/50) + 1}, Sector ${i + 1}`
      },
      lastUpdated: new Date(Date.now() - Math.random() * 3600000).toLocaleTimeString(),
      manager: `Manager ${Math.floor(Math.random() * 100) + 1}`,
      contact: `+91 ${Math.floor(Math.random() * 9000000000) + 1000000000}`
    });
  }
  
  return sites;
};

// Color coding based on attendance rate
const getTileColor = (attendanceRate) => {
  if (attendanceRate >= 90) return 'bg-gradient-to-br from-green-500 to-green-600';
  if (attendanceRate >= 80) return 'bg-gradient-to-br from-green-400 to-green-500';
  if (attendanceRate >= 70) return 'bg-gradient-to-br from-lime-400 to-green-400';
  if (attendanceRate >= 60) return 'bg-gradient-to-br from-yellow-400 to-yellow-500';
  if (attendanceRate >= 50) return 'bg-gradient-to-br from-orange-400 to-orange-500';
  if (attendanceRate >= 40) return 'bg-gradient-to-br from-orange-500 to-red-500';
  return 'bg-gradient-to-br from-red-500 to-red-600';
};

// Get border color based on status
const getStatusBorder = (status) => {
  switch(status) {
    case 'critical': return 'border-red-500 ring-red-500';
    case 'warning': return 'border-yellow-500 ring-yellow-500';
    default: return 'border-green-500 ring-green-500';
  }
};

// Heatmap Tile Component
const HeatmapTile = ({ site, onClick, isSelected }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div
      className={`
        relative rounded-lg p-3 cursor-pointer transition-all duration-300
        ${getTileColor(site.attendanceRate)}
        ${isSelected ? 'ring-4 ring-blue-500 scale-105 shadow-xl' : 'hover:scale-105 hover:shadow-lg'}
        text-white
      `}
      onClick={() => onClick(site)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Location Code */}
      <div className="text-xs font-mono font-bold mb-2 opacity-90">
        {site.code}
      </div>
      
      {/* Four Parameters */}
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          <span>T:{site.total}</span>
        </div>
        <div className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          <span>P:{site.present}</span>
        </div>
        <div className="flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          <span>A:{site.absent}</span>
        </div>
        <div className="flex items-center gap-1">
          <ClockIcon className="w-3 h-3" />
          <span>L:{site.onLeave}</span>
        </div>
      </div>
      
      {/* Attendance Rate Badge */}
      <div className="absolute top-2 right-2 bg-black/30 rounded-full px-1.5 py-0.5 text-xs font-bold">
        {site.attendanceRate}%
      </div>
      
      {/* Hover Tooltip */}
      {isHovered && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white rounded-lg shadow-xl p-3 text-xs pointer-events-none">
          <div className="font-bold mb-2">{site.name}</div>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Location Code:</span>
              <span className="font-mono">{site.code}</span>
            </div>
            <div className="flex justify-between">
              <span>Region:</span>
              <span>{site.region}</span>
            </div>
            <div className="flex justify-between">
              <span>Type:</span>
              <span>{site.type}</span>
            </div>
            <div className="border-t border-gray-700 my-1"></div>
            <div className="flex justify-between">
              <span>Total Staff:</span>
              <span className="font-bold">{site.total}</span>
            </div>
            <div className="flex justify-between">
              <span>Present:</span>
              <span className="text-green-400">{site.present}</span>
            </div>
            <div className="flex justify-between">
              <span>Absent:</span>
              <span className="text-red-400">{site.absent}</span>
            </div>
            <div className="flex justify-between">
              <span>On Leave:</span>
              <span className="text-yellow-400">{site.onLeave}</span>
            </div>
            <div className="border-t border-gray-700 my-1"></div>
            <div className="flex justify-between">
              <span>Attendance Rate:</span>
              <span className={`font-bold ${
                site.attendanceRate >= 80 ? 'text-green-400' :
                site.attendanceRate >= 60 ? 'text-yellow-400' :
                'text-red-400'
              }`}>{site.attendanceRate}%</span>
            </div>
            <div className="flex justify-between">
              <span>Last Updated:</span>
              <span>{site.lastUpdated}</span>
            </div>
            <div className="flex justify-between">
              <span>Manager:</span>
              <span>{site.manager}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Statistics Summary Component
const StatsSummary = ({ sites }) => {
  const stats = useMemo(() => {
    const totalManpower = sites.reduce((sum, s) => sum + s.total, 0);
    const totalPresent = sites.reduce((sum, s) => sum + s.present, 0);
    const totalAbsent = sites.reduce((sum, s) => sum + s.absent, 0);
    const totalOnLeave = sites.reduce((sum, s) => sum + s.onLeave, 0);
    const criticalSites = sites.filter(s => s.status === 'critical').length;
    const warningSites = sites.filter(s => s.status === 'warning').length;
    const normalSites = sites.filter(s => s.status === 'normal').length;
    
    return {
      totalManpower,
      totalPresent,
      totalAbsent,
      totalOnLeave,
      attendanceRate: (totalPresent / totalManpower * 100).toFixed(1),
      criticalSites,
      warningSites,
      normalSites,
      totalSites: sites.length
    };
  }, [sites]);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">Total Sites</span>
          <Building className="w-4 h-4 text-blue-500" />
        </div>
        <p className="text-2xl font-bold">{stats.totalSites}</p>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">Total Manpower</span>
          <Users className="w-4 h-4 text-purple-500" />
        </div>
        <p className="text-2xl font-bold">{stats.totalManpower}</p>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">Present</span>
          <CheckCircle className="w-4 h-4 text-green-500" />
        </div>
        <p className="text-2xl font-bold text-green-600">{stats.totalPresent}</p>
        <p className="text-xs text-gray-500">{((stats.totalPresent / stats.totalManpower) * 100).toFixed(1)}%</p>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">Absent</span>
          <XCircle className="w-4 h-4 text-red-500" />
        </div>
        <p className="text-2xl font-bold text-red-600">{stats.totalAbsent}</p>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">On Leave</span>
          <ClockIcon className="w-4 h-4 text-yellow-500" />
        </div>
        <p className="text-2xl font-bold text-yellow-600">{stats.totalOnLeave}</p>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">Critical Sites</span>
          <AlertTriangle className="w-4 h-4 text-red-500" />
        </div>
        <p className="text-2xl font-bold text-red-600">{stats.criticalSites}</p>
        <p className="text-xs text-gray-500">Need immediate attention</p>
      </div>
    </div>
  );
};

// Site Detail Modal
const SiteDetailModal = ({ site, onClose }) => {
  if (!site) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Site Details</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">✕</button>
        </div>
        
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Building className="w-5 h-5 text-gray-500" />
              <h3 className="text-lg font-semibold">{site.name}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                site.status === 'critical' ? 'bg-red-100 text-red-700' :
                site.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {site.attendanceRate}% Attendance
              </span>
            </div>
            <p className="text-sm text-gray-500">Code: {site.code}</p>
            <p className="text-sm text-gray-500">Region: {site.region} | Type: {site.type}</p>
            <p className="text-sm text-gray-500">Address: {site.location.address}</p>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Total Staff</p>
              <p className="text-2xl font-bold">{site.total}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-xs text-green-600">Present</p>
              <p className="text-2xl font-bold text-green-600">{site.present}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-xs text-red-600">Absent</p>
              <p className="text-2xl font-bold text-red-600">{site.absent}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 text-center">
              <p className="text-xs text-yellow-600">On Leave</p>
              <p className="text-2xl font-bold text-yellow-600">{site.onLeave}</p>
            </div>
          </div>
          
          {/* Shift Breakdown */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3">Shift Breakdown</h4>
            <div className="space-y-3">
              {site.shifts.map((shift, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{shift.name} Shift</span>
                    <span className="text-sm text-gray-500">
                      {((shift.present / shift.total) * 100).toFixed(1)}% present
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-sm">
                    <div>
                      <p className="text-gray-500">Total</p>
                      <p className="font-semibold">{shift.total}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Present</p>
                      <p className="font-semibold text-green-600">{shift.present}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Absent</p>
                      <p className="font-semibold text-red-600">{shift.absent}</p>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${(shift.present / shift.total) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold mb-2">Contact Information</h4>
            <p className="text-sm">Manager: {site.manager}</p>
            <p className="text-sm">Contact: {site.contact}</p>
            <p className="text-sm text-gray-500">Last Updated: {site.lastUpdated}</p>
          </div>
          
          {/* Action Buttons */}
          <div className="mt-6 flex gap-3">
            <button className="flex-1 bg-blue-600 text-white rounded-lg py-2 hover:bg-blue-700 transition-colors">
              Request Resources
            </button>
            <button className="flex-1 border border-gray-300 rounded-lg py-2 hover:bg-gray-50 transition-colors">
              View Reports
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Heatmap Dashboard
const HeatmapDashboard = () => {
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [columns, setColumns] = useState(8);
  
  // Generate mock data
  useEffect(() => {
    refreshData();
  }, []);
  
  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refreshData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);
  
  const refreshData = () => {
    const newData = generateSiteData();
    setSites(newData);
    setLastRefresh(new Date());
  };
  
  // Filter sites
  useEffect(() => {
    let filtered = [...sites];
    
    if (searchTerm) {
      filtered = filtered.filter(site => 
        site.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.region.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterRegion !== 'all') {
      filtered = filtered.filter(site => site.region === filterRegion);
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(site => site.status === filterStatus);
    }
    
    setFilteredSites(filtered);
  }, [sites, searchTerm, filterRegion, filterStatus]);
  
  // Get unique regions for filter
  const regions = useMemo(() => {
    return ['all', ...new Set(sites.map(s => s.region))];
  }, [sites]);
  
  // Calculate grid columns based on view mode
  const gridCols = viewMode === 'compact' ? 12 : columns;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Command Centre Heatmap</h1>
                <p className="text-sm text-gray-500">Real-time Site Monitoring Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                <ClockIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  {lastRefresh.toLocaleTimeString()}
                </span>
              </div>
              
              <button
                onClick={refreshData}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
              
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`p-2 rounded-lg transition-colors ${
                  autoRefresh ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                }`}
              >
                <Activity className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-6 py-6">
        {/* Statistics Summary */}
        <StatsSummary sites={filteredSites} />
        
        {/* Filters and Controls */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by code, name, or region..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {regions.map(region => (
                <option key={region} value={region}>
                  {region === 'all' ? 'All Regions' : region}
                </option>
              ))}
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="normal">Normal</option>
            </select>
            
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('compact')}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === 'compact' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
            
            {viewMode === 'grid' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Columns:</span>
                <select
                  value={columns}
                  onChange={(e) => setColumns(parseInt(e.target.value))}
                  className="px-2 py-1 border border-gray-200 rounded-lg text-sm"
                >
                  <option value={4}>4</option>
                  <option value={6}>6</option>
                  <option value={8}>8</option>
                  <option value={10}>10</option>
                  <option value={12}>12</option>
                </select>
              </div>
            )}
            
            <div className="text-sm text-gray-500">
              Showing {filteredSites.length} of {sites.length} sites
            </div>
          </div>
        </div>
        
        {/* Color Legend */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Attendance Rate:</span>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded"></div>
                <span className="text-xs">≥90%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-500 rounded"></div>
                <span className="text-xs">80-89%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-gradient-to-br from-lime-400 to-green-400 rounded"></div>
                <span className="text-xs">70-79%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded"></div>
                <span className="text-xs">60-69%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-gradient-to-br from-orange-400 to-orange-500 rounded"></div>
                <span className="text-xs">50-59%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-500 rounded"></div>
                <span className="text-xs">40-49%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-6 bg-gradient-to-br from-red-500 to-red-600 rounded"></div>
                <span className="text-xs">&lt;40%</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-500 rounded-full ring-2 ring-red-500"></div>
                <span className="text-xs">Critical</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-yellow-500 rounded-full ring-2 ring-yellow-500"></div>
                <span className="text-xs">Warning</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-500 rounded-full ring-2 ring-green-500"></div>
                <span className="text-xs">Normal</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Heatmap Grid */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-${gridCols} gap-3`}>
          {filteredSites.map(site => (
            <HeatmapTile
              key={site.id}
              site={site}
              onClick={setSelectedSite}
              isSelected={selectedSite?.id === site.id}
            />
          ))}
        </div>
        
        {/* Empty State */}
        {filteredSites.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No sites found matching your filters</p>
          </div>
        )}
      </div>
      
      {/* Site Detail Modal */}
      {selectedSite && (
        <SiteDetailModal
          site={selectedSite}
          onClose={() => setSelectedSite(null)}
        />
      )}
    </div>
  );
};

export default HeatmapDashboard;